import { useState, useEffect, useRef, useCallback } from "react";
import { getReportNote, saveReportNote } from "../../api/api";

export default function ReportNotesPanel({ reportId, onClose }) {
  const [content, setContent] = useState("");
  const [started, setStarted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const debounceRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!reportId) return;
    getReportNote(reportId)
      .then(({ data }) => {
        const text = data.content || "";
        setContent(text);
        if (text) setStarted(true);
      })
      .catch(() => setContent(""))
      .finally(() => setLoading(false));
  }, [reportId]);

  useEffect(() => {
    if (started && !loading) {
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [started, loading]);

  const save = useCallback(async (text) => {
    if (!reportId) return;
    setSaving(true);
    try {
      await saveReportNote(reportId, text);
      setSavedAt(new Date());
    } catch (err) {
      console.error("Failed to save note:", err);
    } finally {
      setSaving(false);
    }
  }, [reportId]);

  const handleChange = (e) => {
    const val = e.target.value;
    setContent(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => save(val), 1200);
  };

  const handleBlur = () => {
    clearTimeout(debounceRef.current);
    save(content);
  };

  const formatTime = (date) =>
    date ? date.toLocaleTimeString("fi-FI", { hour: "2-digit", minute: "2-digit" }) : null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          backgroundColor: "rgba(0,0,0,0.35)",
          zIndex: 1149,
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "min(340px, 92vw)",
          height: "100vh",
          backgroundColor: "#fff",
          zIndex: 1150,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-4px 0 20px rgba(0,0,0,0.18)",
        }}
      >
        {/* Header */}
        <div
          className="d-flex align-items-center justify-content-between px-3 py-2"
          style={{ borderBottom: "1px solid #e9ecef", backgroundColor: "#f8f9fa", flexShrink: 0 }}
        >
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-journal-text" style={{ fontSize: "1.1rem", color: "#198754" }} />
            <span className="fw-semibold" style={{ fontSize: "0.95rem" }}>Muistiinpanot</span>
            <span
              className="badge rounded-pill"
              style={{ fontSize: "0.65rem", backgroundColor: "#e9ecef", color: "#6c757d" }}
            >
              Henkilökohtainen
            </span>
          </div>
          <button className="btn-close btn-sm" onClick={onClose} />
        </div>

        {/* Body */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {loading ? (
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
              <div className="spinner-border spinner-border-sm me-2" />
              Ladataan…
            </div>
          ) : !started ? (
            /* First-time prompt */
            <div
              className="d-flex flex-column align-items-center justify-content-center h-100 px-4 text-center"
              style={{ gap: "1rem" }}
            >
              <div style={{ fontSize: "2.5rem" }}>📝</div>
              <div>
                <div className="fw-semibold mb-1">Henkilökohtainen muistiinpano</div>
                <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                  Luo oma muistiinpano tälle raportille. Vain sinä näet sen.
                </div>
              </div>
              <button
                className="btn btn-success px-4"
                onClick={() => setStarted(true)}
              >
                Luo muistiinpano
              </button>
              <button className="btn btn-link text-muted btn-sm" onClick={onClose}>
                Peruuta
              </button>
            </div>
          ) : (
            /* Textarea */
            <textarea
              ref={textareaRef}
              value={content}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Kirjoita muistiinpanosi tähän…"
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                resize: "none",
                padding: "1rem",
                fontSize: "0.9rem",
                lineHeight: 1.6,
                fontFamily: "inherit",
                backgroundColor: "#fff",
                backgroundImage: "repeating-linear-gradient(to bottom, transparent, transparent calc(1.6em - 1px), #ebebeb calc(1.6em - 1px), #ebebeb 1.6em)",
                backgroundSize: "100% 1.6em",
                backgroundPosition: "0 1rem",
                backgroundAttachment: "local",
              }}
            />
          )}
        </div>

        {/* Footer */}
        {started && !loading && (
          <div
            className="d-flex align-items-center justify-content-between px-3 py-2"
            style={{ borderTop: "1px solid #e9ecef", flexShrink: 0, fontSize: "0.75rem", color: "#6c757d" }}
          >
            <span>
              {saving
                ? "Tallennetaan…"
                : savedAt
                ? `Tallennettu ${formatTime(savedAt)}`
                : "Muutokset tallennetaan automaattisesti"}
            </span>
            <span>{content.length} merkkiä</span>
          </div>
        )}
      </div>
    </>
  );
}
